import React from 'react'
import { Document, Image, Page, Text, View } from '@react-pdf/renderer'

export const statusColors = {
  success: '#059669',
  warning: '#d97706',
  error: '#e11d48',
  na: '#64748b',
}

const statusLabels = {
  success: 'On target',
  warning: 'Near target',
  error: 'Off target',
  na: 'No data',
}

const scoreColor = (healthScore) => {
  if (healthScore === null || healthScore === undefined) return statusColors.na
  if (healthScore >= 80) return statusColors.success
  if (healthScore >= 60) return statusColors.warning
  return statusColors.error
}

// Helvetica (the built-in PDF font) has no ≥/≤ glyphs, so spell the
// direction out instead of printing tofu.
export const targetLabel = (item) =>
  `${item.reverse ? 'max' : 'min'} ${item.target}%`

const formatNumber = (kpi) => {
  if (kpi.value === null || kpi.value === undefined) return '—'
  if (kpi.format === 'minutes') return `${kpi.value}m`
  if (kpi.format === 'currency') return `£${Number(kpi.value).toLocaleString()}`
  if (kpi.format === 'percent') return `${kpi.value}%`
  return Number(kpi.value).toLocaleString()
}

// Column-header row: `columns` is [label, flex, align?] tuples matching the
// flexes of the data rows beneath it.
export const HeaderRow = ({ columns }) => (
  <View
    style={{
      flexDirection: 'row',
      paddingVertical: 2,
      paddingHorizontal: 6,
      borderBottomWidth: 0.5,
      borderBottomColor: '#CBD5E1',
      marginBottom: 1,
    }}
    wrap={false}
  >
    {columns.map(([label, flex, align]) => (
      <Text
        key={label || `${flex}-${align}`}
        style={{
          fontSize: 6.5,
          flex,
          textAlign: align ?? 'left',
          color: '#94a3b8',
          textTransform: 'uppercase',
          letterSpacing: 0.5,
        }}
      >
        {label}
      </Text>
    ))}
  </View>
)

export const Row = ({ children, striped }) => (
  <View
    style={{
      flexDirection: 'row',
      alignItems: 'center',
      paddingVertical: 3,
      paddingHorizontal: 6,
      backgroundColor: striped ? '#F5F7FA' : undefined,
    }}
    wrap={false}
  >
    {children}
  </View>
)

const GaugeRow = ({ kpi, striped }) => (
  <Row striped={striped}>
    <Text style={{ fontSize: 8, flex: 5 }}>{kpi.label}</Text>
    <Text style={{ fontSize: 8, flex: 2, textAlign: 'right' }}>
      {kpi.na ? 'N/A' : `${Math.round(kpi.percent)}%`}
    </Text>
    <Text style={{ fontSize: 8, flex: 2, textAlign: 'right', color: '#64748b' }}>
      {targetLabel(kpi)}
    </Text>
    <Text
      style={{
        fontSize: 8,
        flex: 3,
        textAlign: 'right',
        color: statusColors[kpi.status] ?? statusColors.na,
      }}
    >
      {statusLabels[kpi.status] ?? '—'}
    </Text>
  </Row>
)

const NumberRow = ({ kpi, striped }) => (
  <Row striped={striped}>
    <Text style={{ fontSize: 8, flex: 5 }}>{kpi.label}</Text>
    <Text style={{ fontSize: 8, flex: 2, textAlign: 'right' }}>{formatNumber(kpi)}</Text>
    <Text style={{ fontSize: 8, flex: 2 }} />
    <Text style={{ fontSize: 8, flex: 3 }} />
  </Row>
)

const BreakdownBlock = ({ kpi }) => (
  <View style={{ paddingVertical: 3, paddingHorizontal: 6 }} wrap={false}>
    <Text style={{ fontSize: 8, marginBottom: 2 }}>{kpi.label}</Text>
    {(kpi.items ?? []).slice(0, 6).map((item) => (
      <Text key={item.label} style={{ fontSize: 7, color: '#475569', marginLeft: 8 }}>
        {item.label}: {item.value}
      </Text>
    ))}
    {!kpi.items?.length && (
      <Text style={{ fontSize: 7, color: '#94a3b8', marginLeft: 8 }}>No data in range</Text>
    )}
  </View>
)

const ChartBlock = ({ kpi, image }) => (
  <View style={{ paddingVertical: 4, paddingHorizontal: 6 }} wrap={false}>
    <Text style={{ fontSize: 8, marginBottom: 3, color: '#1F3D7C' }}>{kpi.label}</Text>
    <Image src={image.src} style={{ width: image.width, height: image.height }} />
  </View>
)

// One section's KPIs, grouped like the on-screen page. `charts` is an
// optional { [kpi.key]: { src, width, height } } map (see pdfCharts.js) —
// when a trend/breakdown KPI has an entry its chart is embedded, otherwise
// trends are skipped and breakdowns fall back to a text list.
export const SectionBlock = ({ section, charts }) => {
  const groups = []
  ;(section.kpis ?? []).forEach((kpi) => {
    if (kpi.type === 'trend' && !charts?.[kpi.key]) return
    const groupName = kpi.group ?? 'General'
    let group = groups.find((entry) => entry.name === groupName)
    if (!group) {
      group = { name: groupName, kpis: [] }
      groups.push(group)
    }
    group.kpis.push(kpi)
  })

  return (
    <View style={{ marginTop: 14 }}>
      <View
        style={{
          flexDirection: 'row',
          justifyContent: 'space-between',
          alignItems: 'center',
          backgroundColor: '#1F3D7C',
          paddingVertical: 5,
          paddingHorizontal: 8,
        }}
        wrap={false}
        minPresenceAhead={60}
      >
        <Text style={{ fontSize: 11, color: '#FFFFFF' }}>{section.name}</Text>
        <Text style={{ fontSize: 9, color: '#FFFFFF' }}>
          {section.healthScore === null || section.healthScore === undefined
            ? 'No rated KPIs'
            : `${section.healthScore}% on target (${section.onTarget}/${section.rated})`}
        </Text>
      </View>
      {groups.map((group) => (
        <View key={group.name} style={{ marginTop: 4 }}>
          <Text
            style={{ fontSize: 9, color: '#1F3D7C', paddingHorizontal: 6, marginBottom: 2 }}
            minPresenceAhead={30}
          >
            {group.name}
          </Text>
          {group.kpis.some((kpi) => kpi.type === 'gauge' || kpi.type === 'number') && (
            <HeaderRow
              columns={[
                ['Metric', 5],
                ['Current', 2, 'right'],
                ['Target', 2, 'right'],
                ['Status', 3, 'right'],
              ]}
            />
          )}
          {group.kpis.map((kpi, index) =>
            kpi.type === 'gauge' ? (
              <GaugeRow key={kpi.key} kpi={kpi} striped={index % 2 === 0} />
            ) : kpi.type === 'number' ? (
              <NumberRow key={kpi.key} kpi={kpi} striped={index % 2 === 0} />
            ) : charts?.[kpi.key] ? (
              <ChartBlock key={kpi.key} kpi={kpi} image={charts[kpi.key]} />
            ) : (
              <BreakdownBlock key={kpi.key} kpi={kpi} />
            ),
          )}
        </View>
      ))}
    </View>
  )
}

// Shared page furniture: org header, centred title block, page footer.
export const ReportChrome = ({ orgSettings, title, rangeLabel, printedBy, children }) => (
  <Document options={{ isEvalSupported: false }}>
    <Page
      size="A4"
      style={{ paddingVertical: 40, paddingHorizontal: 30, fontFamily: 'Helvetica' }}
    >
      <View
        style={{
          flexDirection: 'row',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
        fixed
      >
        {orgSettings?.logo ? <Image src={orgSettings.logo} style={{ height: 26 }} /> : <Text />}
        <Text style={{ fontSize: 10, color: 'grey' }}>{orgSettings?.orgName}</Text>
      </View>

      <View style={{ alignItems: 'center', marginTop: 16, gap: 4 }}>
        <Text style={{ fontSize: 18, color: '#3c4e50' }}>{title}</Text>
        <Text style={{ fontSize: 10, color: '#64748b' }}>{rangeLabel}</Text>
        <Text style={{ fontSize: 8, color: '#94a3b8' }}>{printedBy}</Text>
      </View>

      {children}

      <Text
        style={{
          position: 'absolute',
          bottom: 20,
          left: 30,
          right: 30,
          fontSize: 7,
          color: '#94a3b8',
          textAlign: 'center',
        }}
        render={({ pageNumber, totalPages }) =>
          `${orgSettings?.orgName ?? ''} — ${title} — page ${pageNumber} of ${totalPages}`
        }
        fixed
      />
    </Page>
  </Document>
)

// The section's off-target gauges as a table. `sections` may hold one entry
// (section report) or every section (full report).
export const AttentionTable = ({ sections, showSection }) => (
  <View style={{ marginTop: 12 }}>
    <Text style={{ fontSize: 12, color: statusColors.error, marginBottom: 4 }}>
      Needs Attention
    </Text>
    <HeaderRow
      columns={[
        ['KPI', 5],
        ...(showSection ? [['Section', 3]] : []),
        ['Current', 2, 'right'],
        ['Target', 2, 'right'],
      ]}
    />
    {sections.flatMap((section) =>
      (section.attention ?? []).map((item, index) => (
        <Row key={`${section.slug}-${item.key}`} striped={index % 2 === 0}>
          <Text style={{ fontSize: 8, flex: 5 }}>{item.label}</Text>
          {showSection && (
            <Text style={{ fontSize: 8, flex: 3, color: '#64748b' }}>{section.name}</Text>
          )}
          <Text style={{ fontSize: 8, flex: 2, textAlign: 'right', color: statusColors.error }}>
            {Math.round(item.percent)}%
          </Text>
          <Text style={{ fontSize: 8, flex: 2, textAlign: 'right', color: '#64748b' }}>
            {targetLabel(item)}
          </Text>
        </Row>
      )),
    )}
  </View>
)

const KPIReportPDF = ({ sections, orgSettings, rangeLabel, printedBy }) => (
  <ReportChrome
    orgSettings={orgSettings}
    title="KPI & Performance Report"
    rangeLabel={rangeLabel}
    printedBy={printedBy}
  >
    <View style={{ marginTop: 16 }}>
      <Text style={{ fontSize: 12, color: '#1F3D7C', marginBottom: 4 }}>Executive Summary</Text>
      <HeaderRow
        columns={[
          ['Section', 6],
          ['KPIs on target', 3, 'right'],
          ['Score', 2, 'right'],
        ]}
      />
      {sections.map((section, index) => (
        <Row key={section.slug} striped={index % 2 === 0}>
          <Text style={{ fontSize: 9, flex: 6 }}>{section.name}</Text>
          <Text style={{ fontSize: 9, flex: 3, textAlign: 'right', color: '#64748b' }}>
            {section.rated ? `${section.onTarget}/${section.rated} on target` : 'No rated KPIs'}
          </Text>
          <Text
            style={{
              fontSize: 9,
              flex: 2,
              textAlign: 'right',
              color: scoreColor(section.healthScore),
            }}
          >
            {section.healthScore === null || section.healthScore === undefined
              ? '—'
              : `${section.healthScore}%`}
          </Text>
        </Row>
      ))}
    </View>

    {sections.some((section) => section.attention?.length) && (
      <AttentionTable
        sections={sections.filter((section) => section.attention?.length)}
        showSection
      />
    )}

    {sections.map((section) => (
      <SectionBlock key={section.slug} section={section} />
    ))}
  </ReportChrome>
)

export default KPIReportPDF
